import type { Nullish, Satisfies } from '@iheartjane/types';
import isEmpty from 'lodash-es/isEmpty';

import type { AlgoliaProduct } from '@jane/search/types';
import { AlgoliaProductsSchema } from '@jane/search/types';

import type { CurrentSort } from './client/currentSort';
import type { SearchOptions } from './client/types/searchOptions';
import { search } from './search';

type Options = Satisfies<
  {
    attributesToRetrieve?: string[];
    facetFilters?: string[];
    facets?: string[];
    restrictSearchableAttributes?: string[];
  },
  SearchOptions
>;

export type SearchAlgoliaProductsParams = {
  activities?: Nullish<string[]>;
  applicableBrandSpecialIds?: Nullish<string[]>;
  brands?: Nullish<string[]>;
  categories?: Nullish<string[]>;
  feelings?: Nullish<string[]>;
  hasBrandDiscount?: boolean;
  hitsPerPage?: number;
  lineages?: Nullish<string[]>;
  options?: Options;
  page?: number;
  prices?: Nullish<string[]>;
  ratings?: Nullish<string[]>;
  searchText?: Nullish<string>;
  sort?: CurrentSort;
  staticFilters?: string;
  storeIds?: Nullish<number[]>;
  storeSpecificProduct?: boolean;
};

export async function searchAlgoliaProducts({
  activities,
  applicableBrandSpecialIds,
  brands,
  categories,
  feelings,
  hasBrandDiscount,
  hitsPerPage,
  lineages,
  options,
  page,
  prices,
  ratings,
  searchText,
  sort,
  staticFilters,
  storeIds,
  storeSpecificProduct,
}: SearchAlgoliaProductsParams) {
  const searchResponse = await search<AlgoliaProduct>({
    booleanFilters: {
      has_brand_discount: hasBrandDiscount,
      store_specific_product: storeSpecificProduct,
    },
    bucketFilters: {
      aggregate_rating: ratings ?? [],
      bucket_price: prices ?? [],
    },
    filters: {
      activities: activities ?? [],
      applicable_brand_special_ids: applicableBrandSpecialIds ?? [],
      brand: brands ?? [],
      category: lineages ?? [],
      feelings: feelings ?? [],
      root_types: categories ?? [],
      store_id: storeIds ?? [],
    },
    hitsPerPage,
    indexPrefix: `menu-products-`,
    options,
    page,
    searchText,
    currentSort: sort,
    staticFilters,
  });

  if (options?.attributesToRetrieve) {
    return searchResponse;
  } else {
    const { hits, ...rest } = searchResponse;
    const parsed = await AlgoliaProductsSchema.safeParseAsync(hits);
    if (!parsed.success) {
      parsed.error.issues.forEach((e) => console.error(e));
    }

    return {
      hits: parsed.success ? parsed.data : hits,
      ...rest,
    };
  }
}

export type FetchAlgoliaProductParams = {
  productId?: number;
  storeId: number;
  uniqueSlug?: string;
};

export async function fetchAlgoliaProduct({
  productId,
  storeId,
  uniqueSlug,
}: FetchAlgoliaProductParams): Promise<AlgoliaProduct | null> {
  const filters = {
    ...(productId && { product_id: [productId] }),
    ...(uniqueSlug && { unique_slug: [uniqueSlug] }),
    store_id: [storeId],
  };

  const { hits } = await search<AlgoliaProduct>({
    filters,
    indexPrefix: 'menu-products-',
  });
  const parsed = await AlgoliaProductsSchema.safeParseAsync(hits);
  if (!parsed.success) {
    parsed.error.issues.forEach((e) => console.error(e));
  }
  const products = parsed.success ? parsed.data : hits;

  return isEmpty(products) ? null : products[0];
}
